import { StructuredText } from "react-datocms/structured-text";
import React, { useState } from "react";
import sanitizeHtml from "sanitize-html";
import ReactPlayer from "react-player/youtube";
import type {
  ContentBlock,
  CardBlockRecord,
  FloatingCardBlockRecord,
  ImageBlockRecord,
  StructuredTextContent,
  StructuredTextContentWithoutBlocks,
  TableBlockRecord,
  VideoBlockRecord,
} from "@/services/api/types";
import * as Styled from "./styled";
import Modal from "../Modal";
import Button from "../Button";
import FlexContainer from "../FlexContainer";
import { sanitizeHtmlOptions } from "@/utils/sanitize.utils";
import FrozenHeaderTable from "./FrozenHeaderTable";
import Plus from "@assets/icons/zoom-plus-icon.svg?react";
import Enlarge from "@assets/icons/zoom-icon.svg?react";

type Props = {
  content: StructuredTextContent | StructuredTextContentWithoutBlocks;
};

const DatoImageBlock = ({ block }: { block: ImageBlockRecord }) => {
  const [open, setOpen] = useState(false);
  if (block.canOpenInModal) {
    return (
      <Styled.ImageWithModal centered={block.centerAligned}>
        <Styled.ImageWrapper onClick={() => setOpen(true)}>
          <Styled.InlineImage src={block.image.url} alt={block.caption ?? ""} />
          <Styled.HoverImage>
            <Plus />
          </Styled.HoverImage>
        </Styled.ImageWrapper>
        <Styled.ClickToEnlarge onClick={() => setOpen(true)}>
          <Enlarge /> Click to enlarge
        </Styled.ClickToEnlarge>
        <Modal
          modalActive={open}
          toggleModal={() => setOpen((prev) => !prev)}
          autoWidth
        >
          {block.caption}
          <Styled.ModalImage src={block.image.url} alt={block.caption ?? ""} />
        </Modal>
      </Styled.ImageWithModal>
    );
  }
  return (
    <img
      src={block.image.url}
      alt={block.caption ?? ""}
      style={{
        maxWidth: "100%",
        display: block.centerAligned ? "block" : undefined,
        margin: block.centerAligned ? "1rem auto" : "1rem 0",
      }}
    />
  );
};

const renderBlock = (block: ContentBlock) => {
  if (block.__typename === "ImageBlockRecord") {
    return (
      <DatoImageBlock key={block.id} block={block as ImageBlockRecord} />
    );
  }

  if (block.__typename === "TableBlockRecord") {
    const tableBlock = block as TableBlockRecord;
    const sanitizedHtml = sanitizeHtml(tableBlock.table, sanitizeHtmlOptions);
    return <FrozenHeaderTable key={tableBlock.id} html={sanitizedHtml} />;
  }

  if (block.__typename === "VideoBlockRecord") {
    const videoBlock = block as VideoBlockRecord;
    return (
      <FlexContainer padding="1rem 0" maxWidth={600} key={videoBlock.id}>
        <Styled.VideoWrapper>
          <ReactPlayer
            url={videoBlock.youtubeUrl}
            className="react-player"
            width="100%"
            height="100%"
            controls={true}
            config={{ playerVars: { color: "white" } }}
          />
        </Styled.VideoWrapper>
      </FlexContainer>
    );
  }

  if (block.__typename === "CardBlockRecord") {
    const cardBlock = block as CardBlockRecord;
    return (
      <Styled.CardWrapper
        key={cardBlock.id}
        columns={cardBlock.columns ?? "auto"}
      >
        {cardBlock.cards.map((card, i) => (
          <Styled.Card
            key={card.heading || i}
            variant={cardBlock.variant ?? "white"}
          >
            {card.heading && <h5>{card.heading}</h5>}
            {card.text && <DatoBlockRenderer content={card.text} />}
            {card.linkLabel &&
              card.externalLinkPath &&
              !card.internalLinkPath && (
                <Button
                  variant="tertiary"
                  label={card.linkLabel!}
                  externalLink={card.externalLinkPath!}
                />
              )}
            {card.linkLabel &&
              card.internalLinkPath &&
              !card.externalLinkPath && (
                <Button
                  variant="tertiary"
                  label={card.linkLabel!}
                  internalLink={card.internalLinkPath!}
                />
              )}
          </Styled.Card>
        ))}
      </Styled.CardWrapper>
    );
  }

  if (block.__typename === "FloatingCardBlockRecord") {
    const floatingBlock = block as FloatingCardBlockRecord;
    return (
      <Styled.FloatingCardWrapper key={floatingBlock.id}>
        <Styled.FloatingCardText>
          <DatoBlockRenderer content={floatingBlock.text} />
        </Styled.FloatingCardText>
        <Styled.FloatingCard>
          <DatoBlockRenderer content={floatingBlock.cardText} />
        </Styled.FloatingCard>
      </Styled.FloatingCardWrapper>
    );
  }

  return null;
};

const extractText = (children: React.ReactNode): string => {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(extractText).join("");
  if (React.isValidElement(children) && children.props) {
    return extractText(
      (children.props as { children?: React.ReactNode }).children,
    );
  }
  return "";
};

const DatoBlockRenderer = ({ content }: Props) => {
  return (
    <StructuredText
      data={content}
      renderBlock={({ record }) => renderBlock(record as ContentBlock)}
      renderNode={(tagOrComponent, props, ...children) => {
        if (tagOrComponent === "h3") {
          const text = extractText(children as React.ReactNode[]);
          const anchorId = text.replace(/\s+/g, "-").toLowerCase();
          return React.createElement(
            "h3",
            { ...props, id: anchorId },
            ...children,
          );
        }
        return React.createElement(
          tagOrComponent as string,
          props,
          ...children,
        );
      }}
    />
  );
};

export default DatoBlockRenderer;
